/**
 * PulseRow — Horizontal strip of 1-3 metric tiles
 *
 * Renders PulseTile components from the pulse endpoint response.
 * Shows skeleton tiles while loading. Returns null when empty.
 * Max 3 tiles to preserve glanceability on mobile.
 */

import { motion } from "framer-motion";
import { PulseTile } from "./PulseTile";
import type { PulseItem } from "@/hooks/useDashboardPulse";
import type { PulsePeriod, SparklinePoint, SparklineResponse } from "@/hooks/usePulseSparkline";

const SPARKLINE_TILE_IDS = new Set(["orders_today", "revenue_today", "total_products"]);

const SPARKLINE_METRIC_MAP: Record<string, "orders" | "revenue" | "products"> = {
  orders_today: "orders",
  revenue_today: "revenue",
  total_products: "products",
};

// "Today" tiles count the calendar day so far (since midnight) — make that
// explicit so the number doesn't read as contradicting the rolling "last 24h"
// strip elsewhere on the page, and so the weekly trend delta below it isn't
// mistaken for today's change.
const TIMEFRAME_NOTE: Record<string, string> = {
  orders_today: "so far today",
  revenue_today: "so far today",
};

interface PulseRowProps {
  items: PulseItem[];
  isLoading: boolean;
  sparklineData?: SparklineResponse | null;
  isSparklineLoading?: boolean;
  period?: PulsePeriod;
}

export function PulseRow({ items, isLoading, sparklineData, isSparklineLoading, period }: PulseRowProps) {
  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="grid grid-cols-3 gap-3"
      >
        <PulseTile label="" value={0} href="#" isLoading />
        <PulseTile label="" value={0} href="#" isLoading />
        <PulseTile label="" value={0} href="#" isLoading />
      </motion.div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  const visibleItems = items.slice(0, 3);

  const sparklineMap: Record<string, SparklinePoint[] | undefined> = sparklineData
    ? { orders_today: sparklineData.orders, revenue_today: sparklineData.revenue, total_products: sparklineData.products }
    : {};

  const summary = sparklineData?.summary;
  const summaryMap: Record<string, { delta: number; deltaPct: number | null; avg: number } | undefined> = summary
    ? {
        orders_today: { delta: summary.orders_delta, deltaPct: summary.orders_delta_pct, avg: summary.orders_avg },
        revenue_today: { delta: summary.revenue_delta, deltaPct: summary.revenue_delta_pct, avg: summary.revenue_avg },
        total_products: { delta: summary.products_delta, deltaPct: summary.products_delta_pct, avg: summary.products_avg },
      }
    : {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="grid grid-cols-3 gap-3"
    >
      {visibleItems.map((item) => {
        const s = summaryMap[item.id];
        return (
          <PulseTile
            key={item.id}
            label={item.label}
            value={item.value}
            delta={item.delta}
            href={item.href}
            isZero={item.is_zero ?? false}
            sparklineData={sparklineMap[item.id]}
            isSparklineLoading={isSparklineLoading && SPARKLINE_TILE_IDS.has(item.id)}
            sparklineMetric={SPARKLINE_METRIC_MAP[item.id]}
            periodDelta={s?.delta}
            periodDeltaPct={s?.deltaPct}
            periodAvg={s?.avg}
            period={period}
            timeframeNote={TIMEFRAME_NOTE[item.id]}
          />
        );
      })}
    </motion.div>
  );
}
